import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  Chip,
  Divider,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  Publish as PublishIcon,
  History as HistoryIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Translate as TranslateIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import MatchAutocomplete from '../../components/Cricket/MatchAutocomplete';
import GeminiModelSelect from '../../components/GeminiModelSelect';
import MarkdownEditor from '../../components/cms/MarkdownEditor';
import type { Match } from '../../models';
import { delphiService, type GenerationSession, type Iteration, type SessionAsset, type PromptTemplate, type GenerateAssetsRequest, STATIC_BANNER_PLATES } from '../../services/delphiApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Polling configuration constants
const POLLING_CONFIG = {
  MAX_ATTEMPTS: 12,
  INITIAL_DELAY_MS: 1000, // Start with 1 second
  MAX_DELAY_MS: 10000, // Cap at 10 seconds
  BACKOFF_MULTIPLIER: 1.5, // Exponential backoff multiplier
};

const DelphiContentEditor: React.FC = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [session, setSession] = useState<GenerationSession | null>(null);
  const [iterations, setIterations] = useState<Iteration[]>([]);
  const [assets, setAssets] = useState<SessionAsset[]>([]);
  const [currentIteration, setCurrentIteration] = useState<Iteration | null>(null);
  const [loading, setLoading] = useState(false);
  const [refining, setRefining] = useState(false);
  const [generatingAssets, setGeneratingAssets] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [selectedAssetTypes, setSelectedAssetTypes] = useState<SessionAsset['asset_type'][]>([]);
  const [selectedAssets, setSelectedAssets] = useState<number[]>([]);
  const [bannerAspectRatio, setBannerAspectRatio] = useState('16:9');
  const [bannerResolution, setBannerResolution] = useState('2K');
  // 'ai': the image model draws it; 'static': the rules-based banner (team flags, colours, stadium picture), no model call
  const [bannerStyle, setBannerStyle] = useState<'ai' | 'static'>('ai');
  const [bannerPlate, setBannerPlate] = useState('');
  const [translationPrompts, setTranslationPrompts] = useState<PromptTemplate[]>([]);
  const [selectedTranslationPrompt, setSelectedTranslationPrompt] = useState<string | undefined>(undefined);
  const [refinePrompts, setRefinePrompts] = useState<PromptTemplate[]>([]);
  const [selectedRefinePrompt, setSelectedRefinePrompt] = useState<string | undefined>(undefined);
  const [assetPrompts, setAssetPrompts] = useState<PromptTemplate[]>([]);
  const [selectedAssetPrompt, setSelectedAssetPrompt] = useState<string | undefined>(undefined);
  const [bannerPrompts, setBannerPrompts] = useState<PromptTemplate[]>([]);
  const [selectedBannerPrompt, setSelectedBannerPrompt] = useState<string | undefined>(undefined);
  const [baseBannerPrompt, setBaseBannerPrompt] = useState<string>('');
  const [editedBannerPrompt, setEditedBannerPrompt] = useState<string>('');
  const [bannerEditingEnabled, setBannerEditingEnabled] = useState<boolean>(false);
  const [initialPrompts, setInitialPrompts] = useState<PromptTemplate[]>([]);
  const [selectedInitialPrompt, setSelectedInitialPrompt] = useState<string | undefined>(undefined);
  const [additionalInstruction, setAdditionalInstruction] = useState('');
  // Applies to whichever generation action is triggered next (init, refine,
  // regenerate, generate-assets, translate) - empty means use the server
  // default (tier1 for drafts, tier2 for refine/assets/translate).
  const [selectedLlmModel, setSelectedLlmModel] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState<number | null>(null);
  const [showFrozenContextDialog, setShowFrozenContextDialog] = useState(false);
  const [refreshingFrozenContext, setRefreshingFrozenContext] = useState(false);
  const [regeneratingContent, setRegeneratingContent] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTextContent, setEditTextContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const formattedFrozenContext = useMemo(() => {
    if (!session?.frozen_context) return '';
    const raw = JSON.stringify(session.frozen_context, null, 2);
    return raw.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');
  }, [session]);

  // Load session if sessionId is provided
  useEffect(() => {
    if (sessionId && sessionId !== 'new') {
      loadSession(parseInt(sessionId));
    }
  }, [sessionId]);

  // Load translation prompts
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const prompts = await delphiService.listPrompts({ prompt_type: 'translation_hi' });
        setTranslationPrompts(prompts);
        const defaultPrompt = prompts.find((p) => p.is_default);
        setSelectedTranslationPrompt(defaultPrompt?.key);
      } catch (error) {
        // Non-blocking
      }
    };
    fetchPrompts();
  }, []);

  useEffect(() => {
    const fetchOtherPrompts = async () => {
      try {
        const [refine, assets, visual, initial] = await Promise.all([
          delphiService.listPrompts({ prompt_type: 'match_generation' }),
          delphiService.listPrompts({ prompt_type: 'seo' }), // also used for social/visual fallback
          delphiService.listPrompts({ prompt_type: 'visual_prompt' }),
          delphiService.listPrompts({ prompt_type: 'initial_generation' }),
        ]);
        setRefinePrompts(refine);
        setAssetPrompts(assets);
        setBannerPrompts(visual);
        setInitialPrompts(initial);
        const defRefine = refine.find((p) => p.is_default);
        const defAsset = assets.find((p) => p.is_default);
        const defVisual = visual.find((p) => p.is_default);
        const defInitial = initial.find((p) => p.is_default);
        setSelectedRefinePrompt(defRefine?.key);
        setSelectedAssetPrompt(defAsset?.key);
        setSelectedBannerPrompt(defVisual?.key);
        // Initialize base and editable banner prompt text from default visual prompt
        if (defVisual?.user_prompt) {
          setBaseBannerPrompt(defVisual.user_prompt);
          setEditedBannerPrompt(defVisual.user_prompt);
        }
        setSelectedInitialPrompt(defInitial?.key);
      } catch (error) {
        // ignore; UI still works with defaults
      }
    };
    fetchOtherPrompts();
  }, []);

  // Once the session is known, preselect the prompts the server uses for this content: the series' own content and banner
  // prompts when set (series, then tour), else what was selected above (the system defaults). Same rule as the dashboard's
  // draft screen and the automatic drafts, so regenerating here starts from the prompt the draft was written with.
  useEffect(() => {
    if (!session?.object_id || !/match/i.test(session.content_type_name || '')) return;
    let cancelled = false;
    delphiService
      .getPromptDefaults({ app_label: 'cricket', model: 'Match', object_id: session.object_id })
      .then((defaults) => {
        if (cancelled || !defaults) return;
        if (defaults.initial?.key && defaults.initial.source === 'series') setSelectedInitialPrompt(defaults.initial.key);
        if (defaults.visual?.key && defaults.visual.source === 'series') {
          setSelectedBannerPrompt(defaults.visual.key);
          const tpl = bannerPrompts.find((p) => p.key === defaults.visual.key);
          if (tpl?.user_prompt) {
            setBaseBannerPrompt(tpl.user_prompt);
            setEditedBannerPrompt(tpl.user_prompt);
          }
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // bannerPrompts is read once it has loaded; the selection is only made when the session or the prompt list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.object_id, bannerPrompts.length]);

  const loadSession = async (id: number) => {
    setLoading(true);
    try {
      const sessionData = await delphiService.getSession(id);
      setSession(sessionData);
      setIterations(sessionData.iterations || []);
      setAssets(sessionData.assets || []);
      if (sessionData.iterations && sessionData.iterations.length > 0) {
        // Set latest iteration as current
        const latest = sessionData.iterations[sessionData.iterations.length - 1];
        setCurrentIteration(latest);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load session',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInitSession = async () => {
    if (!selectedMatch) {
      setSnackbar({
        open: true,
        message: 'Please select a match',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const newSession = await delphiService.initSession({
        app_label: 'cricket',
        model: 'Match',
        object_id: selectedMatch.id,
        prompt_key: selectedInitialPrompt,
        additional_instruction: additionalInstruction.trim() || undefined,
        llm_model: selectedLlmModel || undefined,
      });
      setSession(newSession);
      setIterations(newSession.iterations || []);
      setAssets(newSession.assets || []);
      if (newSession.iterations && newSession.iterations.length > 0) {
        setCurrentIteration(newSession.iterations[0]);
      }
      navigate(`/delphi/${newSession.id}`, { replace: true });
      setSnackbar({
        open: true,
        message: 'Session initialized successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to initialize session',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async () => {
    if (!session || !refineInstruction.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a refinement instruction',
        severity: 'error',
      });
      return;
    }

    setRefining(true);
    try {
      const newIteration = await delphiService.refineContent(session.id, {
        instruction: refineInstruction,
        prompt_key: selectedRefinePrompt,
        llm_model: selectedLlmModel || undefined,
      });
      setIterations([...iterations, newIteration]);
      setCurrentIteration(newIteration);
      setRefineInstruction('');
      setSnackbar({
        open: true,
        message: 'Content refined successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to refine content',
        severity: 'error',
      });
    } finally {
      setRefining(false);
    }
  };

  const handleGenerateAssets = async () => {
    if (!session || selectedAssetTypes.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one asset type',
        severity: 'error',
      });
      return;
    }

    const requestData: GenerateAssetsRequest = {
      asset_types: selectedAssetTypes,
      iteration_id: currentIteration?.id,
      prompt_key: selectedAssetPrompt,
      visual_prompt_key: selectedBannerPrompt,
      llm_model: selectedLlmModel || undefined,
      async: true,
    };

    // Add banner options if VISUAL_IMAGE is selected
    if (selectedAssetTypes.includes('VISUAL_IMAGE')) {
      requestData.banner_options =
        bannerStyle === 'static'
          ? { style: 'static', plate: bannerPlate || undefined }
          : { aspect_ratio: bannerAspectRatio, resolution: bannerResolution };
      // If user explicitly enabled editing, pass override; otherwise, rely on template
      if (bannerStyle === 'ai' && bannerEditingEnabled && editedBannerPrompt.trim().length > 0) {
        requestData.visual_prompt_override = editedBannerPrompt.trim();
      }
    }
    await runAssetRequest(requestData);
  };

  // Draw the banner straight from the prompt written with this draft (the server reads it from the iteration)
  const handleGenerateSuggestedBanner = async () => {
    if (!session || !currentIteration?.banner_prompt) return;
    await runAssetRequest({
      asset_types: ['VISUAL_IMAGE'],
      iteration_id: currentIteration.id,
      banner_options: { aspect_ratio: bannerAspectRatio, resolution: bannerResolution },
      use_suggested_banner_prompt: true,
      async: true,
    });
  };

  // Put the suggested prompt in the banner text box, editable, to adjust before generating
  const handleUseSuggestedBannerPrompt = () => {
    if (!currentIteration?.banner_prompt) return;
    setBannerEditingEnabled(true);
    setEditedBannerPrompt(currentIteration.banner_prompt);
  };

  const runAssetRequest = async (requestData: GenerateAssetsRequest) => {
    if (!session) return;
    setGeneratingAssets(true);
    try {
      const response = await delphiService.generateAssets(session.id, requestData);
      // If async, response is task info
      if ((response as any).task_id) {
        const taskId = (response as any).task_id as string;
        setSnackbar({
          open: true,
          message: 'Asset generation started...',
          severity: 'info',
        });
        // poll
        pollTask(taskId);
      } else {
        const newAssets = response as any as SessionAsset[];
        setAssets([...assets, ...newAssets]);
        setSelectedAssetTypes([]);
        setSnackbar({
          open: true,
          message: 'Assets generated successfully',
          severity: 'success',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to generate assets',
        severity: 'error',
      });
    } finally {
      setGeneratingAssets(false);
    }
  };

  const calculatePollDelay = (attempts: number): number => {
    return Math.min(
      POLLING_CONFIG.INITIAL_DELAY_MS * Math.pow(POLLING_CONFIG.BACKOFF_MULTIPLIER, attempts),
      POLLING_CONFIG.MAX_DELAY_MS
    );
  };

  const pollTask = async (taskId: string, attempts = 0) => {
    try {
      const status = await delphiService.getTaskStatus(taskId);
      if (status.status === 'SUCCESS' && status.result) {
        const result = typeof status.result === 'string' ? JSON.parse(status.result) : status.result;
        if (result.asset_ids?.length) {
          const refreshedAssets = await delphiService.listAssets(session?.id);
          setAssets(refreshedAssets);
          setSelectedAssetTypes([]);
          setSnackbar({
            open: true,
            message: 'Assets generated successfully',
            severity: 'success',
          });
          return;
        }
      } else if (status.status === 'FAILURE') {
        setSnackbar({
          open: true,
          message: 'Asset generation failed',
          severity: 'error',
        });
        return;
      }
      // Retry with exponential backoff
      if (attempts < POLLING_CONFIG.MAX_ATTEMPTS) {
        const delay = calculatePollDelay(attempts);
        setTimeout(() => pollTask(taskId, attempts + 1), delay);
      } else {
        setSnackbar({
          open: true,
          message: 'Asset generation still in progress. Please refresh later.',
          severity: 'info',
        });
      }
    } catch (error: any) {
      if (attempts < POLLING_CONFIG.MAX_ATTEMPTS) {
        const delay = calculatePollDelay(attempts);
        setTimeout(() => pollTask(taskId, attempts + 1), delay);
      } else {
        setSnackbar({
          open: true,
          message: 'Could not confirm asset generation status. Please refresh.',
          severity: 'warning',
        });
      }
    }
  };

  const handleTranslateToHindi = async () => {
    if (!session || !currentIteration) {
      setSnackbar({
        open: true,
        message: 'Please select an iteration to translate',
        severity: 'error',
      });
      return;
    }

    setTranslating(true);
    try {
      const translationAsset = await delphiService.translateToHindi(
        session.id,
        currentIteration.id,
        selectedTranslationPrompt,
        selectedLlmModel || undefined
      );
      setAssets([...assets, translationAsset]);
      setSnackbar({
        open: true,
        message: 'Content translated to Hindi successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to translate content',
        severity: 'error',
      });
    } finally {
      setTranslating(false);
    }
  };

  const handlePublish = async () => {
    if (!session || !currentIteration) {
      return;
    }

    setPublishing(true);
    try {
      const result = await delphiService.publish(session.id, {
        iteration_id: currentIteration.id,
        asset_ids: selectedAssets.length > 0 ? selectedAssets : undefined,
      });
      setShowPublishDialog(false);
      const message = result.article_id 
        ? `Content published successfully! Article ID: ${result.article_id}`
        : 'Content published successfully';
      setSnackbar({
        open: true,
        message,
        severity: 'success',
      });
      // Reload session to get updated status
      await loadSession(session.id);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to publish content',
        severity: 'error',
      });
    } finally {
      setPublishing(false);
    }
  };

  const handleSelectIteration = (iteration: Iteration) => {
    setCurrentIteration(iteration);
    setEditMode(false);
    setTabValue(0); // Switch to content tab
  };

  const handleEnterEditMode = () => {
    if (!currentIteration) return;
    setEditTitle(currentIteration.title || '');
    setEditSlug(currentIteration.slug || '');
    setEditDescription(currentIteration.description || '');
    setEditTextContent(currentIteration.text_content || '');
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const handleSaveManualEdit = async () => {
    if (!session) return;
    setSavingEdit(true);
    try {
      const newIteration = await delphiService.saveManualEdit(session.id, {
        title: editTitle,
        slug: editSlug,
        description: editDescription,
        text_content: editTextContent,
      });
      setIterations([...iterations, newIteration]);
      setCurrentIteration(newIteration);
      setEditMode(false);
      setSnackbar({
        open: true,
        message: `Saved as version ${newIteration.version}`,
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save edit',
        severity: 'error',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (currentIteration) {
      // Build a structured text with all fields
      const parts: string[] = [];
      
      if (currentIteration.title) {
        parts.push(`# ${currentIteration.title}\n`);
      }
      if (currentIteration.slug) {
        parts.push(`**Slug:** ${currentIteration.slug}\n`);
      }
      if (currentIteration.description) {
        parts.push(`**Summary:** ${currentIteration.description}\n`);
      }
      if (currentIteration.text_content) {
        parts.push(`\n---\n\n${currentIteration.text_content}`);
      }
      
      const fullText = parts.join('\n');
      navigator.clipboard.writeText(fullText);
      setSnackbar({
        open: true,
        message: 'Content copied to clipboard',
        severity: 'success',
      });
    }
  };

  const handleRefreshFrozenContext = async () => {
    if (!session) return;
    if (!window.confirm('Regenerate frozen match data from the latest source?')) {
      return;
    }

    setRefreshingFrozenContext(true);
    try {
      const result = await delphiService.refreshFrozenContext(session.id);
      setSession({ ...session, frozen_context: result.frozen_context });
      setSnackbar({
        open: true,
        message: 'Frozen match data regenerated',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to regenerate frozen data',
        severity: 'error',
      });
    } finally {
      setRefreshingFrozenContext(false);
    }
  };

  const handleRegenerateInitialContent = async () => {
    if (!session) return;
    if (!window.confirm('Regenerate initial content from the initial prompt? This will create a new iteration.')) {
      return;
    }

    setRegeneratingContent(true);
    try {
      const result = await delphiService.regenerateInitialContent(session.id, selectedInitialPrompt, selectedLlmModel || undefined);
      const newIteration = result.iteration;
      setIterations([...iterations, newIteration]);
      setCurrentIteration(newIteration);
      setTabValue(0); // Switch to content tab
      setSnackbar({
        open: true,
        message: 'Initial content regenerated successfully',
        severity: 'success',
      });
      // Reload session to get updated data
      await loadSession(session.id);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to regenerate initial content',
        severity: 'error',
      });
    } finally {
      setRegeneratingContent(false);
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    setDeletingAssetId(assetId);
    try {
      await delphiService.deleteAsset(assetId);
      setAssets(assets.filter((a) => a.id !== assetId));
      setSelectedAssets(selectedAssets.filter((id) => id !== assetId));
      setSnackbar({
        open: true,
        message: 'Asset deleted successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete asset',
        severity: 'error',
      });
    } finally {
      setDeletingAssetId(null);
    }
  };

  if (loading && !session) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/" color="inherit">
          Dashboard
        </MuiLink>
        <MuiLink component={Link} to="/delphi" color="inherit">
          Delphi Sessions
        </MuiLink>
        <Typography color="text.primary">
          {session ? `Session #${session.id}` : 'New Session'}
        </Typography>
      </Breadcrumbs>

      {!session ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Initialize Content Generation Session
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a match and initial prompt template to start generating AI-powered content
          </Typography>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Match
              </Typography>
              <MatchAutocomplete
                value={selectedMatch}
                onChange={setSelectedMatch}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Initial Prompt Template
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Initial Prompt</InputLabel>
                <Select
                  value={selectedInitialPrompt || ''}
                  label="Initial Prompt"
                  onChange={(e) => setSelectedInitialPrompt(e.target.value || undefined)}
                >
                  {initialPrompts.map((p) => (
                    <MenuItem key={p.key} value={p.key}>
                      <Box>
                        <Typography variant="body2">
                          {p.name} {p.is_default && '(Default)'}
                        </Typography>
                        {p.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {p.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                  {initialPrompts.length === 0 && <MenuItem value="">Default</MenuItem>}
                </Select>
              </FormControl>
              {selectedInitialPrompt && initialPrompts.find(p => p.key === selectedInitialPrompt) && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'background.default', maxHeight: 400, overflow: 'auto' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                    System Prompt (Main Instructions):
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem', mb: 2, color: 'primary.main' }}>
                    {initialPrompts.find(p => p.key === selectedInitialPrompt)?.system_prompt}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                    User Instruction:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {initialPrompts.find(p => p.key === selectedInitialPrompt)?.user_prompt}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    Note: System prompt contains Jinja2 templates (e.g., {`{{ match_info.city }}`}) which will be filled with actual match data.
                  </Typography>
                </Paper>
              )}
            </Box>
            <Box sx={{ maxWidth: 300 }}>
              <Typography variant="subtitle2" gutterBottom>
                Gemini Model (Optional)
              </Typography>
              <GeminiModelSelect value={selectedLlmModel} onChange={setSelectedLlmModel} />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Additional Instructions (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Add any specific instructions or context that should be considered during content generation
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Instructions"
                placeholder="e.g., Focus on key player matchups, Emphasize recent form, Include weather conditions impact..."
                value={additionalInstruction}
                onChange={(e) => setAdditionalInstruction(e.target.value)}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleInitSession}
              disabled={!selectedMatch || loading}
              sx={{ alignSelf: 'flex-start' }}
            >
              {loading ? 'Initializing...' : 'Initialize Session'}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Content Generation Session #{session.id}
              </Typography>
              <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" component="span">
                  Status:
                </Typography>
                <Chip label={session.status} size="small" color={session.status === 'completed' ? 'success' : 'default'} />
                {session.published_article && (
                  <Chip
                    label={`Published Article #${session.published_article}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    clickable
                    component="a"
                    href={`/cms/articles/${session.published_article}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                )}
                <Typography variant="body2" color="text.secondary" component="span">
                  {' • '}
                  Iterations: {iterations.length} • Assets: {assets.length}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={() => setShowFrozenContextDialog(true)}
                size="small"
              >
                View Match Data
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshFrozenContext}
                disabled={refreshingFrozenContext || loading}
                size="small"
              >
                {refreshingFrozenContext ? 'Regenerating...' : 'Regenerate Match Data'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => loadSession(session.id)}
                disabled={loading}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          <Paper sx={{ mb: 2 }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="Content" />
              <Tab label="Iterations" icon={<HistoryIcon />} iconPosition="end" />
              <Tab label="Assets" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              {currentIteration ? (
                <Box>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                      Version {currentIteration.version}
                    </Typography>
                    <Box>
                      {editMode ? (
                        <>
                          <Button
                            size="small"
                            startIcon={<CloseIcon />}
                            onClick={handleCancelEdit}
                            disabled={savingEdit}
                            sx={{ mr: 1 }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveManualEdit}
                            disabled={savingEdit}
                          >
                            {savingEdit ? 'Saving...' : 'Save as New Version'}
                          </Button>
                        </>
                      ) : (
                        <>
                          <IconButton onClick={handleEnterEditMode} size="small" title="Edit this version">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={handleCopyToClipboard} size="small" title="Copy to clipboard">
                            <ContentCopyIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>

                  {editMode ? (
                    <Stack spacing={2} sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Title"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Slug"
                        value={editSlug}
                        onChange={(e) => setEditSlug(e.target.value)}
                        sx={{ fontFamily: 'monospace' }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Summary"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                      />
                      <MarkdownEditor
                        label="Content"
                        value={editTextContent}
                        onChange={setEditTextContent}
                        rows={16}
                      />
                    </Stack>
                  ) : (
                    <>
                      {/* Display structured fields separately */}
                      {(currentIteration.title || currentIteration.description || currentIteration.slug) && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                          {currentIteration.title && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                Title:
                              </Typography>
                              <Typography variant="h6">{currentIteration.title}</Typography>
                            </Box>
                          )}
                          {currentIteration.slug && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                Slug:
                              </Typography>
                              <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
                                {currentIteration.slug}
                              </Typography>
                            </Box>
                          )}
                          {currentIteration.description && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                Summary:
                              </Typography>
                              <Typography variant="body2">{currentIteration.description}</Typography>
                            </Box>
                          )}
                        </Paper>
                      )}

                      {/* Content markdown */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: '500px', overflow: 'auto' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                          Content:
                        </Typography>
                        <ReactMarkdown>{currentIteration.text_content}</ReactMarkdown>
                      </Paper>
                    </>
                  )}
                  {!editMode && (
                  <Box sx={{ mb: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Initial Prompt (for Regeneration)</InputLabel>
                        <Select
                          value={selectedInitialPrompt || ''}
                          label="Initial Prompt (for Regeneration)"
                          onChange={(e) => setSelectedInitialPrompt(e.target.value || undefined)}
                        >
                          {initialPrompts.map((p) => (
                            <MenuItem key={p.key} value={p.key}>
                              {p.name} {p.is_default ? '(Default)' : ''}
                            </MenuItem>
                          ))}
                          {initialPrompts.length === 0 && <MenuItem value="">Default</MenuItem>}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleRegenerateInitialContent}
                        disabled={regeneratingContent || session.status === 'completed'}
                        color="secondary"
                      >
                        {regeneratingContent ? 'Regenerating...' : 'Regenerate Initial Content'}
                      </Button>
                      <GeminiModelSelect
                        value={selectedLlmModel}
                        onChange={setSelectedLlmModel}
                        label="Gemini Model (applies below too)"
                        fullWidth={false}
                      />
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Refine Prompt</InputLabel>
                        <Select
                          value={selectedRefinePrompt || ''}
                          label="Refine Prompt"
                          onChange={(e) => setSelectedRefinePrompt(e.target.value || undefined)}
                        >
                          {refinePrompts.map((p) => (
                            <MenuItem key={p.key} value={p.key}>
                              {p.name} {p.is_default ? '(Default)' : ''}
                            </MenuItem>
                          ))}
                          {refinePrompts.length === 0 && <MenuItem value="">Default</MenuItem>}
                        </Select>
                      </FormControl>
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Refinement Instruction"
                      placeholder="e.g., Make it more engaging, Add more statistics, Simplify the language..."
                      value={refineInstruction}
                      onChange={(e) => setRefineInstruction(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={handleRefine}
                      disabled={!refineInstruction.trim() || refining}
                    >
                      {refining ? 'Refining...' : 'Refine Content'}
                    </Button>
                  </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">No iterations yet</Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Stack spacing={2}>
                {iterations.map((iteration) => (
                  <Card
                    key={iteration.id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      border: currentIteration?.id === iteration.id ? 2 : 1,
                      borderColor: currentIteration?.id === iteration.id ? 'primary.main' : 'divider',
                    }}
                    onClick={() => handleSelectIteration(iteration)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Box>
                          <Typography variant="h6">Version {iteration.version}</Typography>
                          {iteration.version === 1 && (
                            <Chip label="Initial Generation" size="small" color="primary" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                        <Chip label={new Date(iteration.created_at).toLocaleString()} size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Instruction:</strong> {iteration.user_instruction}
                      </Typography>
                      <Typography variant="body2" sx={{ maxHeight: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {iteration.text_content.substring(0, 200)}...
                      </Typography>
                      {iteration.llm_metadata?.tokens && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Tokens: {iteration.llm_metadata.tokens.total_tokens} • Model: {iteration.llm_metadata.model}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Generate Assets
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Asset Prompt</InputLabel>
                    <Select
                      value={selectedAssetPrompt || ''}
                      label="Asset Prompt"
                      onChange={(e) => setSelectedAssetPrompt(e.target.value || undefined)}
                    >
                      {assetPrompts.map((p) => (
                        <MenuItem key={p.key} value={p.key}>
                          {p.name} {p.is_default ? '(Default)' : ''}
                        </MenuItem>
                      ))}
                      {assetPrompts.length === 0 && <MenuItem value="">Default</MenuItem>}
                    </Select>
                  </FormControl>
                  <GeminiModelSelect value={selectedLlmModel} onChange={setSelectedLlmModel} fullWidth={false} />
                </Stack>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Select Asset Types</FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssetTypes.includes('SEO_META')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssetTypes([...selectedAssetTypes, 'SEO_META']);
                            } else {
                              setSelectedAssetTypes(selectedAssetTypes.filter((t) => t !== 'SEO_META'));
                            }
                          }}
                        />
                      }
                      label="SEO Meta Tags"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssetTypes.includes('SOCIAL_X')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssetTypes([...selectedAssetTypes, 'SOCIAL_X']);
                            } else {
                              setSelectedAssetTypes(selectedAssetTypes.filter((t) => t !== 'SOCIAL_X'));
                            }
                          }}
                        />
                      }
                      label="Social Media - X (Twitter)"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssetTypes.includes('SOCIAL_FB')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssetTypes([...selectedAssetTypes, 'SOCIAL_FB']);
                            } else {
                              setSelectedAssetTypes(selectedAssetTypes.filter((t) => t !== 'SOCIAL_FB'));
                            }
                          }}
                        />
                      }
                      label="Social Media - Facebook"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssetTypes.includes('SOCIAL_INSTAGRAM')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssetTypes([...selectedAssetTypes, 'SOCIAL_INSTAGRAM']);
                            } else {
                              setSelectedAssetTypes(selectedAssetTypes.filter((t) => t !== 'SOCIAL_INSTAGRAM'));
                            }
                          }}
                        />
                      }
                      label="Social Media - Instagram"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssetTypes.includes('SOCIAL_TELEGRAM')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssetTypes([...selectedAssetTypes, 'SOCIAL_TELEGRAM']);
                            } else {
                              setSelectedAssetTypes(selectedAssetTypes.filter((t) => t !== 'SOCIAL_TELEGRAM'));
                            }
                          }}
                        />
                      }
                      label="Social Media - Telegram"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssetTypes.includes('VISUAL_IMAGE')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssetTypes([...selectedAssetTypes, 'VISUAL_IMAGE']);
                            } else {
                              setSelectedAssetTypes(selectedAssetTypes.filter((t) => t !== 'VISUAL_IMAGE'));
                            }
                          }}
                        />
                      }
                      label="Match Banner Image"
                    />
                  </FormGroup>
                </FormControl>

                {selectedAssetTypes.includes('VISUAL_IMAGE') && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel>Banner style</InputLabel>
                      <Select
                        value={bannerStyle}
                        label="Banner style"
                        onChange={(e) => setBannerStyle(e.target.value as 'ai' | 'static')}
                      >
                        <MenuItem value="ai">AI image (Gemini)</MenuItem>
                        <MenuItem value="static">Static (flags, team colours, stadium)</MenuItem>
                      </Select>
                    </FormControl>
                    {bannerStyle === 'static' && (
                      <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Picture</InputLabel>
                        <Select value={bannerPlate} label="Picture" onChange={(e) => setBannerPlate(e.target.value)}>
                          {STATIC_BANNER_PLATES.map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                              {p.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                )}
                {selectedAssetTypes.includes('VISUAL_IMAGE') && bannerStyle === 'ai' && currentIteration?.banner_prompt && (
                  <Box
                    sx={{ mt: 2, p: 2, border: 1, borderColor: 'primary.main', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    <Typography variant="subtitle2">Banner prompt written for this match</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Written with this draft. It describes the players and the stadium without naming anyone.
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {currentIteration.banner_prompt}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleGenerateSuggestedBanner}
                        disabled={generatingAssets}
                      >
                        Generate banner with this prompt
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleUseSuggestedBannerPrompt} disabled={generatingAssets}>
                        Edit before generating
                      </Button>
                    </Stack>
                  </Box>
                )}
                {selectedAssetTypes.includes('VISUAL_IMAGE') && bannerStyle === 'ai' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, mt: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Banner Prompt</InputLabel>
                      <Select
                        value={selectedBannerPrompt || ''}
                        label="Banner Prompt"
                        onChange={(e) => {
                          const key = e.target.value || undefined;
                          setSelectedBannerPrompt(key);
                          const tpl = bannerPrompts.find((p) => p.key === key);
                          const base = tpl?.user_prompt || '';
                          setBaseBannerPrompt(base);
                          // Always reflect latest selection in the textarea
                          setEditedBannerPrompt(base);
                        }}
                      >
                        {bannerPrompts.map((p) => (
                          <MenuItem key={p.key} value={p.key}>
                            {p.name} {p.is_default ? '(Default)' : ''}
                          </MenuItem>
                        ))}
                        {bannerPrompts.length === 0 && <MenuItem value="">Default</MenuItem>}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      label="Banner Prompt"
                      placeholder="Visual prompt used for banner generation..."
                      value={editedBannerPrompt}
                      onChange={(e) => setEditedBannerPrompt(e.target.value)}
                      disabled={!bannerEditingEnabled}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bannerEditingEnabled}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            setBannerEditingEnabled(enabled);
                            // When enabling editing, ensure textarea has current base prompt
                            if (enabled && !editedBannerPrompt.trim() && baseBannerPrompt) {
                              setEditedBannerPrompt(baseBannerPrompt);
                            }
                          }}
                        />
                      }
                      label="Enable editing (send as override when generating banner)"
                    />
                  </Box>
                )}
                {selectedAssetTypes.includes('VISUAL_IMAGE') && bannerStyle === 'ai' && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Banner Image Options
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Aspect Ratio</InputLabel>
                        <Select
                          value={bannerAspectRatio}
                          label="Aspect Ratio"
                          onChange={(e) => setBannerAspectRatio(e.target.value)}
                        >
                          <MenuItem value="1:1">1:1</MenuItem>
                          <MenuItem value="2:3">2:3</MenuItem>
                          <MenuItem value="3:2">3:2</MenuItem>
                          <MenuItem value="3:4">3:4</MenuItem>
                          <MenuItem value="4:3">4:3</MenuItem>
                          <MenuItem value="4:5">4:5</MenuItem>
                          <MenuItem value="5:4">5:4</MenuItem>
                          <MenuItem value="9:16">9:16</MenuItem>
                          <MenuItem value="16:9">16:9</MenuItem>
                          <MenuItem value="21:9">21:9</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Resolution</InputLabel>
                        <Select
                          value={bannerResolution}
                          label="Resolution"
                          onChange={(e) => setBannerResolution(e.target.value)}
                        >
                          <MenuItem value="1K">1K</MenuItem>
                          <MenuItem value="2K">2K</MenuItem>
                          <MenuItem value="4K">4K</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                )}

                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerateAssets}
                  disabled={selectedAssetTypes.length === 0 || generatingAssets}
                  sx={{ mt: 2 }}
                >
                  {generatingAssets ? 'Generating...' : 'Generate Assets'}
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Generated Assets
              </Typography>
              <Stack spacing={2}>
                {assets.map((asset) => (
                  <Card key={asset.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Chip label={asset.asset_type} size="small" />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (selectedAssets.includes(asset.id)) {
                                setSelectedAssets(selectedAssets.filter((id) => id !== asset.id));
                              } else {
                                setSelectedAssets([...selectedAssets, asset.id]);
                              }
                            }}
                            title="Select for publishing"
                          >
                            {selectedAssets.includes(asset.id) ? <CheckCircleIcon color="primary" /> : <RadioButtonUncheckedIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAsset(asset.id)}
                            disabled={deletingAssetId === asset.id}
                            title="Delete asset"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      {asset.asset_type === 'VISUAL_IMAGE' && asset.content.image_url ? (
                        <Box>
                          {asset.content.error ? (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              {asset.content.error}
                            </Alert>
                          ) : null}
                          <Box sx={{ mb: 1 }}>
                            <img
                              src={asset.content.image_url}
                              alt={asset.content.filename || 'Match banner'}
                              style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Aspect Ratio: {asset.content.aspect_ratio} • Resolution: {asset.content.resolution}
                            {asset.content.filename && ` • ${asset.content.filename}`}
                          </Typography>
                        </Box>
                      ) : asset.asset_type === 'TRANSLATION_HI' ? (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Hindi Translation
                          </Typography>
                          {asset.content.title && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">Title:</Typography>
                              <Typography variant="body2">{asset.content.title}</Typography>
                            </Box>
                          )}
                          {asset.content.description && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">Description:</Typography>
                              <Typography variant="body2">{asset.content.description}</Typography>
                            </Box>
                          )}
                          {asset.content.content_markdown && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">Content:</Typography>
                              <Paper variant="outlined" sx={{ p: 1, mt: 0.5, maxHeight: '200px', overflow: 'auto' }}>
                                <ReactMarkdown>{asset.content.content_markdown}</ReactMarkdown>
                              </Paper>
                            </Box>
                          )}
                          {asset.content.seo && Object.keys(asset.content.seo).length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">SEO (Hindi):</Typography>
                              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {JSON.stringify(asset.content.seo, null, 2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : asset.asset_type === 'SOCIAL_TELEGRAM' ? (
                        <Box sx={{ bgcolor: 'rgba(0, 136, 204, 0.08)', p: 2, borderRadius: 1, border: '1px solid rgba(0, 136, 204, 0.25)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#0088cc', mb: 1, fontWeight: 600 }}>
                            Telegram Channel Teaser
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>
                            {asset.content?.message || (typeof asset.content === 'string' ? asset.content : JSON.stringify(asset.content, null, 2))}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {JSON.stringify(asset.content, null, 2)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {assets.length === 0 && (
                  <Typography color="text.secondary">No assets generated yet</Typography>
                )}
              </Stack>
            </TabPanel>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Translation Prompt</InputLabel>
              <Select
                value={selectedTranslationPrompt || ''}
                label="Translation Prompt"
                onChange={(e) => setSelectedTranslationPrompt(e.target.value || undefined)}
              >
                {translationPrompts.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.name} {p.is_default ? '(Default)' : ''}
                  </MenuItem>
                ))}
                {translationPrompts.length === 0 && <MenuItem value="">Default</MenuItem>}
              </Select>
            </FormControl>
            <GeminiModelSelect value={selectedLlmModel} onChange={setSelectedLlmModel} fullWidth={false} />
            <Button
              variant="outlined"
              startIcon={<TranslateIcon />}
              onClick={handleTranslateToHindi}
              disabled={!currentIteration || translating || session.status === 'completed'}
            >
              {translating ? 'Translating...' : 'Translate to Hindi'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PublishIcon />}
              onClick={() => setShowPublishDialog(true)}
              disabled={!currentIteration || session.status === 'completed' || editMode}
            >
              Publish
            </Button>
          </Box>

          <Dialog open={showPublishDialog} onClose={() => setShowPublishDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Publish Content</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                Are you sure you want to publish this content?
              </Typography>
              {session.published_article && (
                <Alert severity="info" sx={{ mb: 1, mt: 1 }}>
                  Linked to <strong>Article #{session.published_article}</strong>. Publishing will update this existing article.
                </Alert>
              )}
              {selectedAssets.length > 0 && selectedAssets.every(id => {
                const asset = assets.find(a => a.id === id);
                return asset?.asset_type.startsWith('SOCIAL_');
              }) && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  Only social media sharing (Telegram/etc.) will be updated. The article title, body, and translations will remain untouched.
                </Alert>
              )}
              {currentIteration && (
                <Typography variant="body2" color="text.secondary">
                  Version {currentIteration.version} will be published.
                </Typography>
              )}
              {selectedAssets.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedAssets.length} asset(s) will be included.
                </Typography>
              )}
              {assets.some(a => a.asset_type === 'TRANSLATION_HI' && (selectedAssets.includes(a.id) || selectedAssets.length === 0)) && (
                <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'medium' }}>
                  ✓ Hindi translations will be included
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPublishDialog(false)}>Cancel</Button>
              <Button onClick={handlePublish} variant="contained" disabled={publishing}>
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog 
            open={showFrozenContextDialog} 
            onClose={() => setShowFrozenContextDialog(false)} 
            maxWidth="lg" 
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">Frozen Match Data</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Captured at session initialization • Used as context for AI generation
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(formattedFrozenContext);
                    setSnackbar({
                      open: true,
                      message: 'Match data copied to clipboard',
                      severity: 'success',
                    });
                  }}
                  title="Copy JSON to clipboard"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {/* Key match info section */}
                {session.frozen_context.match_info && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                        Match Information
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1.5 }}>
                        {Object.entries(session.frozen_context.match_info).map(([key, value]) => (
                          <Box key={key}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              {key.replace(/_/g, ' ')}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {typeof value === 'object' && value !== null 
                                ? JSON.stringify(value) 
                                : String(value || 'N/A')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Teams section */}
                {(session.frozen_context.team_a || session.frozen_context.team_b) && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                        Teams
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        {session.frozen_context.team_a && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>Team A</Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                              <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(session.frozen_context.team_a, null, 2)}
                              </pre>
                            </Paper>
                          </Box>
                        )}
                        {session.frozen_context.team_b && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>Team B</Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                              <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(session.frozen_context.team_b, null, 2)}
                              </pre>
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Full raw data section */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                      Complete Raw Data (JSON)
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        maxHeight: '40vh', 
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem'
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {formattedFrozenContext}
                      </pre>
                    </Paper>
                  </CardContent>
                </Card>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRefreshFrozenContext} disabled={refreshingFrozenContext}>
                {refreshingFrozenContext ? 'Regenerating...' : 'Regenerate Data'}
              </Button>
              <Button onClick={() => setShowFrozenContextDialog(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DelphiContentEditor;

